import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, RotateCw } from "lucide-react";
import StatsBar from "./StatsBar";
import ReportRow from "./ReportRow";
import ReportDetail from "./ReportDetail";

const FILTERS = ["all", "harness", "frustration", "feature_request", "general"];

export default function HarnessReports() {
    const [reports, setReports] = useState([]);
    const [stats, setStats] = useState({ total: 0, unresolvedCount: 0, statsByCategory: [] });
    const [filter, setFilter] = useState("all");
    const [resolvedFilter, setResolvedFilter] = useState("false");
    const [selectedReport, setSelectedReport] = useState(null);

    const { call: fetchReports, isLoading } = useBluelagoon({ method: "getHarnessReports" });
    const { call: resolve } = useBluelagoon({ method: "resolveHarnessReport" });

    const loadReports = useCallback(async () => {
        const params = { resolved: resolvedFilter };
        if (filter !== "all") params.category = filter === "feature_request" ? "feature" : filter;
        const res = await fetchReports(params);
        if (res?.data) {
            setReports(res.data.reports || []);
            setStats({ total: res.data.total, unresolvedCount: res.data.unresolvedCount, statsByCategory: res.data.statsByCategory || [] });
        }
    }, [fetchReports, filter, resolvedFilter]);

    useEffect(() => { loadReports(); }, [loadReports]);

    const handleResolve = useCallback(async (reportId, resolved) => {
        await resolve({ reportId, resolved });
        loadReports();
        if (selectedReport?.id === reportId) setSelectedReport(null);
    }, [resolve, loadReports, selectedReport]);

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Harness Reports</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Feedback and frustration reports from AI agents
                    </p>
                </div>
                <Button variant="outline" onClick={loadReports} disabled={isLoading}>
                    <RotateCw className="h-4 w-4 mr-2" />Refresh
                </Button>
            </div>

            <StatsBar total={stats.total} unresolved={stats.unresolvedCount} categories={stats.statsByCategory} />

            <div className="flex gap-2 flex-wrap">
                {FILTERS.map((f) => (
                    <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)}>
                        {f === "all" ? "All" : f.replace("_", " ")}
                    </Button>
                ))}
                <div className="ml-4 flex gap-2">
                    {[["false", "Open"], ["true", "Resolved"]].map(([val, label]) => (
                        <Button key={val} variant={resolvedFilter === val ? "default" : "outline"} size="sm" onClick={() => setResolvedFilter(val)}>
                            {label}
                        </Button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : reports.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No reports found.</CardContent></Card>
            ) : (
                <div className="space-y-2">
                    {reports.map((r) => (
                        <ReportRow key={r.id} report={r} onSelect={setSelectedReport} onResolve={handleResolve} selected={selectedReport?.id === r.id} />
                    ))}
                </div>
            )}

            {selectedReport && (
                <ReportDetail report={selectedReport} onClose={() => setSelectedReport(null)} onResolve={handleResolve} />
            )}
        </div>
    );
}
